import React, { useMemo, useCallback } from 'react';
import { InputNumber } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { SbTable, SbButton, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import SbTag from 'uiCore/SbTag/SbTag';
import { getJdStatusBadge, JD_STATUS_FILTERS, jdStatusOnFilter } from 'constants/jobs.constants';
import { COMP_PAGE_SIZE, COMP_PAGE_SIZE_OPTIONS } from 'constants/compensation.constants';
import {
  CompTableWrapperStyled,
  SourceBadgeStyled,
  EditIconButton,
  EditCellWrapper,
  EllipsisCell,
  UpdatedByCell,
  UpdatedByEmail,
  UpdatedByDate,
} from './CompensationManagement.styles';

const TOOLTIP_EDIT_COMPENSATION = "Edit the compensation data for this job code inline.";

const formatNumber = (val) => {
  if (val == null || val === '') return '-';
  return Number(val).toLocaleString('en-US');
};

const getDisplayJobTitle = (record) => record?.job_title_for_jd || record?.job_title;

const CompensationTable = ({
  dataSource = [],
  hasSpecialty = false,
  loading = false,
  tableFilters = {},
  onTableChange,
  editingRowId,
  editValues,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  savingRowId,
  updatedJobIds = new Set(),
}) => {
  const safeData = useMemo(
    () => (dataSource || []).filter(
      (row) => row && (row.job_code || row.job_title || row.sb_unique_id),
    ),
    [dataSource],
  );

  const isEditing = useCallback(
    (record) => record?.job_id === editingRowId,
    [editingRowId],
  );

  // Accepts a field key or an accessor fn, so columns that render a derived
  // value (e.g. Job Title) can filter on exactly what the cell shows.
  const getFilters = useCallback(
    (keyOrAccessor) => {
      const getValue = typeof keyOrAccessor === 'function'
        ? keyOrAccessor
        : (item) => item?.[keyOrAccessor];
      return [...new Set((safeData || []).map(getValue).filter(Boolean))]
        .map((value) => ({ text: value, value }));
    },
    [safeData],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Job Code',
        dataIndex: 'job_code',
        key: 'job_code',
        filters: getFilters('job_code'),
        filteredValue: tableFilters.job_code || null,
        onFilter: (value, record) => record?.job_code === value,
        render: (text) => text || '-',
      },
      {
        title: 'Job Title',
        dataIndex: 'job_title',
        key: 'job_title',
        filters: getFilters(getDisplayJobTitle),
        filteredValue: tableFilters.job_title || null,
        onFilter: (value, record) => getDisplayJobTitle(record) === value,
        filterSearch: true,
        render: (_, record) => getDisplayJobTitle(record) || '-',
      },
      ...(hasSpecialty ? [{
        title: 'Specialty',
        dataIndex: 'specialty',
        key: 'specialty',
        filters: getFilters('specialty'),
        filteredValue: tableFilters.specialty || null,
        onFilter: (value, record) => record?.specialty === value,
        filterSearch: true,
        width: 160,
        ellipsis: { showTitle: false },
        render: (text) => (
          <SbTooltip content={text} disabled={!text}>
            <EllipsisCell>{text || '-'}</EllipsisCell>
          </SbTooltip>
        ),
      }] : []),
      {
        title: 'Status',
        dataIndex: 'jd_status',
        key: 'jd_status',
        filters: JD_STATUS_FILTERS,
        filteredValue: tableFilters.jd_status || null,
        onFilter: jdStatusOnFilter,
        render: (status) => {
          if (!status) return '-';
          // Same label/color/tooltip mapping as the Jobs Library status badge
          // (getJdStatusBadge, constants/jobs.constants) -- kept in sync there,
          // not re-derived here, since a bulk-upload-only view has no live
          // JD-generation task to layer a "Processing Failed" state on top of.
          const { label, color, tooltip } = getJdStatusBadge(status);
          return (
            <SbTooltip content={tooltip}>
              <SbTag color={color}>{label}</SbTag>
            </SbTooltip>
          );
        },
      },
      {
        title: 'Function',
        dataIndex: 'function',
        key: 'function',
        filters: getFilters('function'),
        filteredValue: tableFilters.function || null,
        onFilter: (value, record) => record?.function === value,
        render: (text) => text || '-',
      },
      {
        title: 'Legal Entity',
        dataIndex: 'legal_entity',
        key: 'legal_entity',
        filters: getFilters('legal_entity'),
        filteredValue: tableFilters.legal_entity || null,
        onFilter: (value, record) => record?.legal_entity === value,
        render: (text) => text || '-',
      },
      {
        title: 'Job Level',
        dataIndex: 'job_level',
        key: 'job_level',
        filters: getFilters('job_level'),
        filteredValue: tableFilters.job_level || null,
        onFilter: (value, record) => record?.job_level === value,
        render: (text) => text || '-',
      },
      {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        filters: getFilters('location'),
        filteredValue: tableFilters.location || null,
        onFilter: (value, record) => record?.location === value,
        render: (text) => text || '-',
      },
      {
        title: 'Avg Comp (Local)',
        dataIndex: 'local_compensation',
        key: 'compensation_local',
        filteredValue: tableFilters.compensation_local || null,
        render: (text, record) => {
          if (isEditing(record)) {
            return (
              <EditCellWrapper>
                <InputNumber
                  value={editValues?.local_compensation}
                  onChange={(val) => onEditChange('local_compensation', val)}
                  min={0}
                  size="small"
                  formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                  parser={(value) => value.replace(/,/g, '')}
                />
              </EditCellWrapper>
            );
          }
          if (text == null) return '-';
          const local_currency = record?.local_currency || 'USD';
          return `${formatNumber(text)} ${local_currency}`;
        },
      },
      {
        title: 'Avg Comp (HQ)',
        dataIndex: 'compensation',
        key: 'compensation_hq',
        filteredValue: tableFilters.compensation_hq || null,
        render: (text, record) => {
          if (text == null) return '-';
          const hqCurrency = record?.currency || 'USD';
          return `${formatNumber(text)} ${hqCurrency}`;
        },
      },
      {
        title: 'No. of Positions',
        dataIndex: 'total_positions',
        key: 'total_positions',
        filteredValue: tableFilters.total_positions || null,
        render: (text, record) => {
          if (isEditing(record)) {
            return (
              <InputNumber
                value={editValues?.total_positions}
                onChange={(val) => onEditChange('total_positions', val)}
                min={0}
                precision={0}
                size="small"
              />
            );
          }
          return text != null ? text : '-';
        },
      },
      {
        title: 'Source',
        dataIndex: 'compensation_source',
        key: 'compensation_source',
        filters: getFilters('compensation_source'),
        filteredValue: tableFilters.compensation_source || null,
        onFilter: (value, record) => record?.compensation_source === value,
        render: (text) => {
          if (!text) return '-';
          return <SourceBadgeStyled $source={text}>{text}</SourceBadgeStyled>;
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'compensation_updated_by',
        key: 'updated_by',
        filteredValue: tableFilters.updated_by || null,
        width: '14%',
        render: (_, record) => {
          const owner = record?.compensation_updated_by || '-';
          let formattedDate = '';
          if (record?.fx_timestamp) {
            try {
              formattedDate = new Date(record.fx_timestamp).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              });
            } catch {
              formattedDate = record.fx_timestamp;
            }
          }
          return (
            <UpdatedByCell>
              <UpdatedByEmail>{owner}</UpdatedByEmail>
              {formattedDate && <UpdatedByDate>{formattedDate}</UpdatedByDate>}
            </UpdatedByCell>
          );
        },
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => {
          if (isEditing(record)) {
            const isSaving = savingRowId === record.job_id;
            return (
              <EditCellWrapper>
                <SbButton
                  buttonType="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => onEditSave(record)}
                  loading={isSaving}
                  disabled={isSaving}
                />
                <SbButton
                  buttonType="secondary"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onEditCancel}
                  disabled={isSaving}
                />
              </EditCellWrapper>
            );
          }
          return (
            <SbTooltip content={TOOLTIP_EDIT_COMPENSATION}>
              <TooltipTriggerStyled>
                <EditIconButton
                  type="button"
                  onClick={() => onEditStart(record)}
                  aria-label="Edit compensation"
                >
                  <EditOutlined />
                </EditIconButton>
              </TooltipTriggerStyled>
            </SbTooltip>
          );
        },
      },
    ],
    [safeData, hasSpecialty, tableFilters, editingRowId, editValues, isEditing, getFilters, onEditStart, onEditChange, onEditSave, onEditCancel, savingRowId],
  );

  return (
    <CompTableWrapperStyled>
      <SbTable
        columns={columns}
        dataSource={safeData}
        loading={loading}
        rowKey={(record, index) => record?.job_id ?? record?.sb_unique_id ?? index}
        rowClassName={(record) => {
          const classes = [];
          if (isEditing(record)) classes.push('comp-editing-row');
          if (updatedJobIds.has(record?.job_id)) classes.push('comp-bulk-updated-row');
          return classes.join(' ');
        }}
        onChange={onTableChange}
        pagination={{
          defaultPageSize: COMP_PAGE_SIZE,
          pageSizeOptions: COMP_PAGE_SIZE_OPTIONS,
          showSizeChanger: true,
          size: 'small',
        }}
        scroll={{ y: 'calc(100vh - 355px)' }}
        sticky={{ offsetHeader: 0 }}
        size="small"
      />
    </CompTableWrapperStyled>
  );
};

export default CompensationTable;
